import { AuthController } from "@somewear/auth";
import type {
	FirebaseConfirmationResult,
	FirebaseRecaptchaVerifier,
	IFirebaseAuthService,
} from "@somewear/firebase";
import { firebaseRecaptchaVerifier } from "@somewear/firebase";
import { Sentry } from "@somewear/model";
import { setUserPhone } from "@web/app/appSlice";
import type { IErrorLoading } from "@web/common/Common";
import ControlledInputController from "@web/common/ControlledInputController";
import type { DispatchProps } from "@web/common/Interfaces";
import Loader from "@web/common/Loader";
import Paths from "@web/common/Paths";
import PhoneInputController from "@web/common/PhoneInputController";
import { modifyState } from "@web/common/utils";
import * as React from "react";
import { connect } from "react-redux";
import type { RouteComponentProps } from "react-router";
import { Redirect, withRouter } from "react-router";

import PhoneView from "./PhoneView";
import VerifyView from "./VerifyView";

interface IPhoneControllerState extends IErrorLoading {
	confirmation?: FirebaseConfirmationResult;
}

export enum PhoneError {
	AlreadyInUse = "auth/credential-already-in-use",
	InvalidCode = "auth/invalid-verification-code",
	Expired = "auth/code-expired",
}

type Props = RouteComponentProps & DispatchProps;

class PhoneController extends React.Component<Props, IPhoneControllerState> {
	phoneInput: PhoneInputController;
	verifyInput: ControlledInputController;
	recaptcha?: FirebaseRecaptchaVerifier;

	constructor(props: Props) {
		super(props);

		this.state = {
			error: undefined,
			loading: false,
			confirmation: undefined,
		};

		this.solveCaptcha = this.solveCaptcha.bind(this);
		this.verifyCode = this.verifyCode.bind(this);
		this.phoneInput = new PhoneInputController();
		this.verifyInput = new ControlledInputController({
			enterAction: this.verifyCode,
		});
	}

	solveCaptcha(evt: React.FormEvent<HTMLButtonElement>) {
		evt.preventDefault();
		if (!this.phoneInput.isValid()) {
			const error = { message: "Invalid phone number" };
			this.setState(modifyState(this.state, { error: error }));
			return;
		}
		this.setState(modifyState(this.state, { loading: true }));
		this.recaptcha &&
			this.recaptcha.verify().catch((error) => {
				console.log(error);
				if (error.message === "reCAPTCHA has already been rendered in this element") {
					window.location.reload();
				} else {
					this.setState(
						modifyState(this.state, {
							loading: false,
							error: {
								message: "Whoops something went wrong, please try again",
							},
						}),
					);
				}
			});
	}

	componentDidMount() {
		// test numbers 650-555-3434 650-555-3433
		this.recaptcha = firebaseRecaptchaVerifier(
			() => this.sendText(),
			() => {
				this.setState(modifyState(this.state, { loading: false }));
			},
		);
	}

	sendText() {
		const user = AuthController.service.getCurrentAuthUser();
		if (user !== undefined) {
			// we logged in via another provider, now linking phone number
			if (user?.email?.includes("someweartest")) {
				this.props.history.push(Paths.Subscription);
				return;
			}
			try {
				(AuthController.service as IFirebaseAuthService)
					.linkWithPhoneNumber(this.phoneInput.getValue(), this.recaptcha!)
					.subscribe(
						(confirmation) => {
							this.setState(
								modifyState(this.state, {
									confirmation: confirmation,
									loading: false,
								}),
							);
						},
						(error) => {
							this.setState(
								modifyState(this.state, {
									error: error,
									loading: false,
								}),
							);
							console.error(error);
							Sentry.captureException(error);
						},
					);
			} catch (error) {
				console.error(error);
				this.setState(modifyState(this.state, { loading: false }));
				Sentry.captureException(error);
			}
		} else {
			// we're signing in via phone
			try {
				(AuthController.service as IFirebaseAuthService)
					.loginWithPhoneNumber(this.phoneInput.getValue(), this.recaptcha!)
					.subscribe(
						(confirmation) => {
							this.setState(
								modifyState(this.state, {
									confirmation: confirmation,
									loading: false,
								}),
							);
						},
						(error) => {
							this.setState(
								modifyState(this.state, {
									error: error,
									loading: false,
								}),
							);
							console.error(error);
							Sentry.captureException(error);
						},
					);
			} catch (error) {
				console.error(error);
				this.setState(modifyState(this.state, { loading: false }));
				Sentry.captureException(error);
			}
		}
	}

	verifyCode() {
		if (!this.verifyInput.isValid()) {
			this.setState(modifyState(this.state, { error: { message: "Invalid code" } }));
			return;
		}

		this.setState(modifyState(this.state, { loading: true }));
		//call rest api to verify code
		this.state
			.confirmation!.confirm(this.verifyInput.getValue())
			.then(() => {
				const user = AuthController.service.getCurrentAuthUser();
				if (user !== undefined && user.email === undefined) {
					// we don't have an email yet for this phone number
					(AuthController.service as IFirebaseAuthService).deleteUser().subscribe(() => {
						alert(
							"There is no account associated with this phone number. Please create an account first.",
						);
						this.props.history.push(Paths.Reset);
					});
				} else if (user !== undefined) {
					// linking the phone number to another auth provider
					this.props.dispatch(setUserPhone(this.verifyInput.getValue()));
					this.props.history.push(Paths.Subscription);
				} else {
					this.props.dispatch(setUserPhone(this.verifyInput.getValue()));
					this.props.history.push(Paths.App);
				}
			})
			.catch((error) => {
				console.log(error);
				if (error.code === PhoneError.AlreadyInUse) {
					error.message =
						"The phone number is already associated with a different user account";
				} else if (error.code === PhoneError.InvalidCode) {
					error.message = "Invalid code";
				} else if (error.code === PhoneError.Expired) {
					error.message =
						"THE SMS CODE HAS EXPIRED. PLEASE RE-SEND THE VERIFICATION CODE TO TRY AGAIN.";
				} else {
					Sentry.captureException(error);
				}
				this.setState(modifyState(this.state, { error: error, loading: false }));
			});
	}

	render(): React.ReactNode {
		const user = AuthController.service.getCurrentAuthUser();

		if (user?.phoneNumber !== undefined) {
			return <Redirect to={Paths.Subscription} />;
		}
		if (this.state.loading) {
			return <Loader />;
		}
		if (this.state.confirmation) {
			return (
				<VerifyView
					{...this.state}
					verifyController={this.verifyInput}
					verifyCode={this.verifyCode}
					hideSignOut={user === undefined}
				/>
			);
		} else
			return (
				<PhoneView
					{...this.state}
					phoneInputController={this.phoneInput}
					solveCaptcha={this.solveCaptcha}
					hideSignOut={user === undefined}
				/>
			);
	}
}

export default connect()(withRouter(PhoneController));
